import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArrowUp, FaWhatsapp, FaRobot, FaTimes, FaPaperPlane } from 'react-icons/fa';
import './FloatingActions.css';

function FloatingActions() {
  const [showTop, setShowTop] = useState(false);
  const [settings, setSettings] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [qaList, setQaList] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [hasWhatsappNotif, setHasWhatsappNotif] = useState(true);
  const [hasChatNotif, setHasChatNotif] = useState(true);
  const chatBodyRef = useRef(null);

  useEffect(() => {
    axios.get('https://api.hospifylink.com/api/footer/settings').then(r => setSettings(r.data)).catch(() => {});
    axios.get('https://api.hospifylink.com/api/chatbot/qa').then(r => setQaList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [messages, chatOpen]);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const openWhatsApp = () => {
    setHasWhatsappNotif(false);
    const number = settings?.whatsapp_number || '250789951922';
    const msg = settings?.whatsapp_message || 'Hello Hospify Link team!';
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const openChat = () => {
    setHasChatNotif(false);
    setChatOpen(true);
    if (messages.length === 0) {
      setMessages([{ sender: 'bot', text: 'Hi! 👋 Welcome to Hospify Link. Ask me about chefs, housekeepers, receptionists, cleaners or how to book.' }]);
    }
  };

  const askQuestion = (qa) => {
    setMessages(prev => [...prev, { sender: 'user', text: qa.question }, { sender: 'bot', text: qa.answer }]);
  };

  const sendText = (e) => {
    e.preventDefault();
    const text = inputText.trim();
    if (!text) return;
    const lower = text.toLowerCase();
    const match = qaList.find(qa =>
      qa.question.toLowerCase().includes(lower) ||
      qa.question.toLowerCase().split(' ').some(word => word.length > 3 && lower.includes(word))
    );
    setMessages(prev => [...prev, { sender: 'user', text }]);
    setInputText('');
    setTimeout(() => {
      setMessages(prev => [...prev, {
        sender: 'bot',
        text: match ? match.answer : 'I\'m not sure yet. 🙏 Try a quick question below or reach us on WhatsApp.'
      }]);
    }, 400);
  };

  return (
    <>
      <button className={`float-btn float-top ${showTop ? 'float-show' : ''}`}
        onClick={scrollTop} title="Back to top" aria-label="Back to top">
        <FaArrowUp />
      </button>

      <div className="float-right-group">
        <button className="float-btn float-whatsapp" onClick={openWhatsApp}
          title="Chat on WhatsApp" aria-label="WhatsApp">
          <FaWhatsapp />
          <span className="float-pulse" />
          {hasWhatsappNotif && <span className="float-notif-dot" />}
        </button>

        <button className={`float-btn float-chatbot ${chatOpen ? 'float-active' : ''}`}
          onClick={chatOpen ? () => setChatOpen(false) : openChat}
          title="Chat with us" aria-label="Chatbot">
          {chatOpen ? <FaTimes /> : <FaRobot />}
          {hasChatNotif && !chatOpen && <span className="float-notif-dot" />}
        </button>
      </div>

      {chatOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar"><FaRobot /></div>
              <div>
                <h4>Hospify Assistant</h4>
                <span>Online • Replies instantly</span>
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setChatOpen(false)}><FaTimes /></button>
          </div>

          <div className="chatbot-body" ref={chatBodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chatbot-msg chatbot-${m.sender}`}>
                {m.sender === 'bot' && <div className="chatbot-msg-avatar"><FaRobot /></div>}
                <div className="chatbot-msg-bubble">{m.text}</div>
              </div>
            ))}
            {qaList.length > 0 && messages.length <= 1 && (
              <div className="chatbot-quick">
                <p className="chatbot-quick-title">Quick questions:</p>
                {qaList.slice(0, 6).map(q => (
                  <button key={q.id} className="chatbot-quick-btn" onClick={() => askQuestion(q)}>
                    {q.question}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form className="chatbot-input-row" onSubmit={sendText}>
            <input type="text" placeholder="Type your question..." value={inputText}
              onChange={e => setInputText(e.target.value)} />
            <button type="submit" aria-label="Send"><FaPaperPlane /></button>
          </form>
        </div>
      )}
    </>
  );
}

export default FloatingActions;
